/**
 * Guarded service worker registration.
 *
 * The SW must NEVER register inside the Lovable editor preview iframe or on
 * Lovable preview/staging hosts — it causes stale content, navigation
 * interference, and persistent cache pollution. It also must not register in
 * dev mode (we set `devOptions.enabled: false` in vite.config, but we guard
 * here too as defense in depth).
 *
 * On disallowed hosts we proactively unregister any pre-existing SW and
 * delete its caches — this protects users who previously visited when the SW
 * was incorrectly registered.
 */

const BLOCKED_HOST_PATTERNS = [
  /id-preview--.*\.lovable\.app$/i,
  /preview--.*\.lovable\.app$/i,
  /.*\.lovableproject\.com$/i,
  /.*\.lovableproject-dev\.com$/i,
];

const isInIframe = (): boolean => {
  try {
    return window.self !== window.top;
  } catch {
    // Cross-origin access throws → we're embedded.
    return true;
  }
};

const isBlockedHost = (hostname: string): boolean =>
  BLOCKED_HOST_PATTERNS.some((re) => re.test(hostname));

export const isPwaAllowed = (): boolean => {
  if (typeof window === "undefined") return false;
  if (!("serviceWorker" in navigator)) return false;
  if (import.meta.env.DEV) return false;
  if (isInIframe()) return false;
  if (isBlockedHost(window.location.hostname)) return false;
  return true;
};

/**
 * Unregister any leftover service workers and clear their caches.
 * Called on disallowed hosts so previously-installed SWs can't keep
 * serving stale content to editors / preview iframes.
 */
const purgeServiceWorkers = async (): Promise<void> => {
  if (!("serviceWorker" in navigator)) return;
  try {
    const regs = await navigator.serviceWorker.getRegistrations();
    await Promise.all(regs.map((r) => r.unregister()));
    if ("caches" in window) {
      const names = await caches.keys();
      await Promise.all(names.map((n) => caches.delete(n)));
    }
  } catch {
    /* best-effort cleanup */
  }
};

type UpdateHandler = (reload: () => void) => void;

/**
 * Register the Workbox-generated SW with a controlled update flow.
 * Returns a cleanup function (no-op when SW is not allowed).
 */
export const registerServiceWorker = async (
  onUpdate?: UpdateHandler,
): Promise<() => void> => {
  if (!isPwaAllowed()) {
    // Aggressively clean up on disallowed hosts so old SWs can't haunt us.
    await purgeServiceWorkers();
    return () => {};
  }

  // Dynamic import keeps workbox-window out of the critical path on first paint.
  const { Workbox } = await import("workbox-window");
  const wb = new Workbox("/sw.js");

  wb.addEventListener("waiting", () => {
    onUpdate?.(() => {
      wb.addEventListener("controlling", () => window.location.reload());
      wb.messageSkipWaiting();
    });
  });

  try {
    await wb.register();
  } catch (err) {
    // Never let a SW registration failure break the app.
    // eslint-disable-next-line no-console
    console.warn("[pwa] service worker registration failed", err);
  }

  return () => {
    /* nothing to tear down — keep API symmetric */
  };
};
